import { Lightbulb } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "src/common/utils";
import { BlurFade } from "src/components/ui/blur-fade";
import { ChatComposer } from "src/components/ui/chat/ChatComposer";
import type { ChatAttachmentType } from "src/components/ui/chat/useChatAttachments";

import { TipType } from "../types";

interface EmptyStateChatProps {
  onSendMessage: (message: string, attachments?: ChatAttachmentType[]) => Promise<unknown> | unknown;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  tips?: TipType[];
  isSending?: boolean;
  prefilledQuestion?: string;
  hideHeader?: boolean;
  compact?: boolean;
}

const TIP_ROTATION_INTERVAL = 17000; // 17 seconds

const EmptyStateChat: React.FC<EmptyStateChatProps> = ({
  onSendMessage,
  messagesEndRef,
  tips = [],
  isSending = false,
  prefilledQuestion = "",
  hideHeader = false,
  compact = false,
}) => {
  const [inputValue, setInputValue] = useState(prefilledQuestion);
  const [currentTipIndex, setCurrentTipIndex] = useState(() =>
    tips.length > 0 ? Math.floor(Math.random() * tips.length) : 0
  );
  const [isFading, setIsFading] = useState(false);
  const chatInputRef = useRef<HTMLTextAreaElement & { setInputMessage?: (message: string) => void }>(null);

  // Set prefilled question on mount
  useEffect(() => {
    if (prefilledQuestion && chatInputRef.current) {
      setInputValue(prefilledQuestion);
      if (chatInputRef.current.setInputMessage) {
        chatInputRef.current.setInputMessage(prefilledQuestion);
      }
      chatInputRef.current.focus();
    } else if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, [prefilledQuestion]);

  // Rotate tips every 17 seconds with fade transition (random selection)
  useEffect(() => {
    if (tips.length <= 1) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const interval = setInterval(() => {
      setIsFading(true);
      timeoutId = setTimeout(() => {
        setCurrentTipIndex((prev) => {
          // Pick a random index different from the current one
          let next;
          do {
            next = Math.floor(Math.random() * tips.length);
          } while (next === prev);
          return next;
        });
        setIsFading(false);
      }, 300); // Fade out duration
    }, TIP_ROTATION_INTERVAL);

    return () => {
      clearInterval(interval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [tips.length]);

  // Handle sending a message
  const handleSendMessage = useCallback(async (message: string, attachments: ChatAttachmentType[] = []) => {
    await onSendMessage(message, attachments);
    setInputValue("");
  }, [onSendMessage]);

  const currentTip = tips[currentTipIndex];

  return (
    <div className="flex-1 p-6 pb-12 flex flex-col items-center justify-center text-center text-foreground overflow-auto">
      <div className={cn("w-full max-w-5xl", !hideHeader && !compact && "-mt-8")}>
        {compact ? (
          <BlurFade duration={0.5}>
            <h2 className="text-xl font-medium mb-6 text-foreground tracking-tight">
              How can I help you?
            </h2>
          </BlurFade>
        ) : !hideHeader && (
          <>
            <BlurFade duration={0.8}>
              <h1 className="text-3xl md:text-4xl font-normal mb-3 text-primary tracking-tight">
                How can I help you today?
              </h1>
            </BlurFade>

            <BlurFade delay={0.1} duration={0.8}>
              <p className="text-sm md:text-base text-foreground mb-12">
                Search across your code, logs, metrics, traces, and deploys to answer questions and investigate issues
              </p>
            </BlurFade>
          </>
        )}

        <div className="w-full mt-8">
          <form className="relative rounded-md bg-popover border border-border p-1.5 shadow-sm focus-within:border-primary/50 transition-colors duration-150">
            <ChatComposer
              ref={chatInputRef}
              placeholder="Ask anything about your systems"
              onSend={handleSendMessage}
              defaultValue={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={isSending}
              sendDisabled={isSending}
              inputClassName="min-h-[44px] w-full resize-none border-0 bg-transparent py-3 text-foreground text-sm shadow-none focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              attachmentButtonClassName="left-1.5"
            />
            <div ref={messagesEndRef} />
          </form>
        </div>

        {/* Tips Section - subtle, non-blocking hint */}
        {tips.length > 0 && currentTip && (
          <BlurFade delay={0.3} duration={0.8}>
            <div className="mt-8">
              <div
                className="inline-flex items-center gap-2 text-xs text-muted-foreground transition-opacity duration-300"
                style={{ opacity: isFading ? 0 : 1 }}
              >
                <Lightbulb className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  <span className="font-medium text-foreground">Tips:</span>{" "}
                  {currentTip.text}
                  {currentTip.url && currentTip.linkText && (
                    <>
                      {" "}
                      {currentTip.url.startsWith("http") ? (
                        <a
                          href={currentTip.url}
                          className="text-foreground hover:text-primary underline underline-offset-2 transition-colors"
                        >
                          {currentTip.linkText}
                        </a>
                      ) : (
                        <Link
                          to={currentTip.url}
                          className="text-foreground hover:text-primary underline underline-offset-2 transition-colors"
                        >
                          {currentTip.linkText}
                        </Link>
                      )}
                    </>
                  )}
                </span>
              </div>
            </div>
          </BlurFade>
        )}
      </div>
    </div>
  );
};

export default EmptyStateChat;
